import { useEffect } from "react";
import { FaLeaf, FaComments, FaChartLine, FaShieldAlt } from "react-icons/fa";
import "../assets/styles/NutrIAPage.css";
import BotaoContato from "../components/common/BotaoContato";
import Footer20 from "../components/common/Footer20";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */
const plans = [
  {
    name: "Piloto",
    price: "R$ 0,00",
    features: [
      "Configuração rápida",
      "2 semanas de nutrição ativa",
      "Sequência básica de 3 mensagens",
      "Relatório inicial de engajamento",
    ],
    cta: "Iniciar Piloto",
    popular: false,
    color: "#16a34a",
    textColor: "#fff",
    waMessage: "Gostaria de ativar o produto NutrIA no plano Piloto",
  },
  {
    name: "Essencial",
    price: "R$ 89,90/mês",
    features: [
      "Sequências automáticas por WhatsApp",
      "Mensagens adaptadas ao comportamento do lead",
      "Relatórios semanais",
      "Suporte básico",
    ],
    cta: "Assinar Essencial",
    popular: true,
    color: "#FFC300",
    textColor: "#0d1b2a",
    waMessage: "Gostaria de ativar o produto NutrIA no plano Essencial",
  },
  {
    name: "Pro",
    price: "R$ 399,90/mês",
    features: [
      "Sequências totalmente personalizadas",
      "IA adaptativa por perfil de lead",
      "Integração CRM",
      "Notificação ao vendedor no momento certo",
      "Suporte prioritário",
    ],
    cta: "Assinar Pro",
    popular: false,
    color: "#7c3aed",
    textColor: "#fff",
    waMessage: "Gostaria de ativar o produto NutrIA no plano Pro",
  },
];

const faqs = [
  {
    q: "Funciona com qualquer fonte de leads?",
    a: "Sim — NutrIA recebe leads do LeadIA, de formulários do seu site, planilhas ou qualquer CRM. Funciona de forma independente.",
  },
  {
    q: "Posso aprovar as mensagens antes do envio?",
    a: "Sim — todos os templates são revisados e aprovados por você antes da ativação. Controle total sobre o tom da sua marca.",
  },
  {
    q: "Como a IA sabe quando acionar o vendedor?",
    a: "Monitoramos sinais de interesse do lead — respostas, cliques, perguntas — e notificamos seu time no momento mais quente.",
  },
  {
    q: "O lead vai perceber que é automático?",
    a: "As mensagens são personalizadas e variam automaticamente, soando naturais e humanizadas em cada interação.",
  },
];

/* ─────────────────────────────────────────
   HOOK
───────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".nu-reveal");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ─────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────── */
function Navbar() {
  return (
    <nav className="nu-nav">
      <a href="/" className="nu-nav-logo">Summer<span>Tech</span></a>
      <a href="/" className="nu-nav-back">← Voltar para início</a>
      <a href="#cta" className="nu-nav-cta">Ativar NutrIA →</a>
    </nav>
  );
}

function MockupNutrIA() {
  return (
    <div className="nu-hero-visual">
      <div className="nu-mockup">
        <div className="nu-mock-bar">
          <div className="nu-mock-dot" style={{ background: "#ff5f57" }} />
          <div className="nu-mock-dot" style={{ background: "#febc2e" }} />
          <div className="nu-mock-dot" style={{ background: "#28c840" }} />
          <span className="nu-mock-title">NutrIA · Sequências Ativas</span>
        </div>
        <div className="nu-mock-body">
          <div className="nu-mock-row">
            <span className="nu-mock-icon">🌱</span>
            <div className="nu-mock-info">
              <span className="nu-mock-label">Lead recebido</span>
              <span className="nu-mock-val">João Silva — interessado em plano odonto</span>
            </div>
            <span className="nu-mock-tag nu-tag-yellow">Novo</span>
          </div>
          <div className="nu-mock-row">
            <span className="nu-mock-icon">💬</span>
            <div className="nu-mock-info">
              <span className="nu-mock-label">Mensagem enviada · Dia 2</span>
              <span className="nu-mock-val">Oi João! Vi seu interesse 😊 Separei um case...</span>
            </div>
            <span className="nu-mock-tag nu-tag-green">Enviado</span>
          </div>
          <div className="nu-mock-row">
            <span className="nu-mock-icon">🔥</span>
            <div className="nu-mock-info">
              <span className="nu-mock-label">Lead aquecido · Dia 7</span>
              <span className="nu-mock-val">João respondeu — notificando vendedor</span>
            </div>
            <span className="nu-mock-tag nu-tag-blue">Quente</span>
          </div>
          <div className="nu-mock-stats">
            <div className="nu-mock-stat">
              <span className="nu-mock-stat-num">83%</span>
              <span className="nu-mock-stat-lbl">Taxa abertura</span>
            </div>
            <div className="nu-mock-stat">
              <span className="nu-mock-stat-num">31</span>
              <span className="nu-mock-stat-lbl">Leads ativos</span>
            </div>
            <div className="nu-mock-stat">
              <span className="nu-mock-stat-num">12</span>
              <span className="nu-mock-stat-lbl">Convertidos</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  return (
    <section className="nu-hero">
      <div className="nu-hero-glow" />
      <div className="nu-hero-glow2" />
      <div className="nu-hero-inner">
        <div className="nu-hero-text">
          <span className="nu-hero-badge">
            <span className="nu-badge-dot" />
            Novo produto · SummerTech
          </span>
          <h1><span className="nu-accent">Nutri</span>A</h1>
          <p className="nu-hero-tagline">Cultive seus leads até a conversão — no automático</p>
          <p>
            NutrIA acompanha cada lead com mensagens personalizadas pelo WhatsApp, adaptando
            o ritmo e o conteúdo conforme o comportamento de cada pessoa — e avisa seu time
            exatamente quando o lead está pronto para fechar.
          </p>
          <div className="nu-hero-actions">
            <a href="#cta" className="nu-btn-primary">Ativar NutrIA</a>
            <a href="#como" className="nu-btn-ghost">Como funciona</a>
          </div>
        </div>
        <MockupNutrIA />
      </div>
    </section>
  );
}

function SobreSection() {
  return (
    <section className="nu-section nu-sobre">
      <div className="nu-wrap">
        <div className="nu-sobre-grid nu-reveal">
          <div>
            <span className="nu-tag">O Produto</span>
            <h2 className="nu-title">O que é NutrIA</h2>
            <p className="nu-text">
              NutrIA é a solução que cuida do lead depois que ele chegou — seja pelo LeadIA,
              pelo seu site ou por qualquer outra fonte. Com sequências automáticas de WhatsApp
              personalizadas por IA, ela mantém o relacionamento aquecido até o momento certo
              da venda, sem precisar de intervenção humana constante.
            </p>
          </div>
          <div className="nu-sobre-cards">
            {[
              { icon: "🌱", title: "Sequências inteligentes", text: "Mensagens automáticas enviadas no momento certo, adaptadas ao perfil e comportamento de cada lead." },
              { icon: "💬", title: "Humanizado e personalizado", text: "Cada mensagem soa natural — com variação de texto e tom aprovado por você. Sem robôs aparentes." },
              { icon: "🔔", title: "Alerta na hora H", text: "Quando o lead demonstra interesse real, seu vendedor é notificado imediatamente para fechar." },
            ].map((c) => (
              <div className="nu-info-card" key={c.title}>
                <div className="nu-info-icon">{c.icon}</div>
                <div>
                  <div className="nu-info-title">{c.title}</div>
                  <div className="nu-info-text">{c.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ComoSection() {
  const steps = [
    { icon: <FaLeaf />, num: "01", title: "Recebe o lead", desc: "NutrIA recebe o lead de qualquer fonte — LeadIA, formulário, planilha ou CRM — e inicia o acompanhamento automaticamente." },
    { icon: <FaComments />, num: "02", title: "Nutre com mensagens", desc: "Sequências de WhatsApp personalizadas são enviadas nos momentos ideais, adaptando conteúdo e tom ao perfil do lead." },
    { icon: <FaChartLine />, num: "03", title: "Converte e notifica", desc: "Quando o lead aquece, seu time é avisado na hora certa. Relatórios semanais mostram o desempenho de cada sequência." },
  ];
  return (
    <section className="nu-section nu-como" id="como">
      <div className="nu-wrap">
        <span className="nu-tag">Como Funciona</span>
        <h2 className="nu-title">Três etapas.<br />Lead convertido.</h2>
        <div className="nu-steps">
          {steps.map((s) => (
            <div className="nu-step nu-reveal" key={s.num}>
              <div className="nu-step-num">{s.num}</div>
              <span className="nu-step-icon">{s.icon}</span>
              <div className="nu-step-title">{s.title}</div>
              <div className="nu-step-desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeneficiosSection() {
  const bens = [
    { num: "+Conversão", title: "Sem deixar lead esfriar", text: "Acompanhamento contínuo garante que nenhum lead seja perdido por falta de follow-up." },
    { num: "24/7", title: "Nutrição ininterrupta", text: "NutrIA trabalha enquanto você dorme — nenhuma oportunidade passa despercebida." },
    { num: "IA", title: "Adaptação em tempo real", text: "A IA ajusta frequência e conteúdo conforme o lead responde ou interage com as mensagens." },
    { num: "100%", title: "Controle da mensagem", text: "Você aprova todos os templates antes da ativação. Sua marca sempre bem representada." },
  ];
  return (
    <section className="nu-section nu-beneficios">
      <div className="nu-wrap">
        <span className="nu-tag">Benefícios</span>
        <h2 className="nu-title">Por que NutrIA<br />transforma seu funil</h2>
        <div className="nu-ben-grid">
          {bens.map((b) => (
            <div className="nu-ben-card nu-reveal" key={b.title}>
              <span className="nu-ben-num">{b.num}</span>
              <div className="nu-ben-title">{b.title}</div>
              <div className="nu-ben-text">{b.text}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SegurancaSection() {
  const items = [
    "Mensagens com variação automática — nunca repetitivas ou suspeitas.",
    "Limites de envio configuráveis para cada perfil de lead.",
    "Blacklist de termos e opt-out automático por solicitação.",
    "Logs completos de cada interação realizada.",
    "Dados tratados em conformidade com a LGPD.",
  ];
  return (
    <section className="nu-section nu-seg">
      <div className="nu-wrap">
        <div className="nu-seg-inner nu-reveal">
          <div className="nu-seg-quote">
            "Relacionamento contínuo, humanizado e em conformidade — do piloto ao plano Pro."
          </div>
          <div>
            <span className="nu-tag">Segurança e Compliance</span>
            <div className="nu-seg-items">
              {items.map((item) => (
                <div className="nu-seg-item" key={item}>
                  <FaShieldAlt className="nu-seg-item-icon" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section className="nu-section nu-pricing">
      <div className="nu-wrap">
        <span className="nu-tag">Planos</span>
        <h2 className="nu-title">Piloto rápido.<br />Escale depois.</h2>
        <p className="nu-pricing-subtitle">
          Comece com nosso piloto gratuito de 2 semanas e veja seus leads sendo nutridos antes de qualquer compromisso.
        </p>
        <div className="nu-plans">
          {plans.map((p) => (
            <article className={`nu-plan nu-reveal ${p.popular ? "popular" : ""}`} key={p.name}>
              {p.popular && <div className="nu-plan-badge">Mais popular</div>}
              <div className="nu-plan-name">{p.name}</div>
              <div className="nu-plan-price">{p.price}</div>
              <ul className="nu-plan-features">
                {p.features.map((f) => (
                  <li key={f}>
                    <span className="nu-plan-check">✔</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <BotaoContato className="nu-plan-btn" text={p.cta} mensagem={p.waMessage} style={{ backgroundColor: p.color, color: p.textColor }}/>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="nu-section nu-faq">
      <div className="nu-wrap">
        <span className="nu-tag">Dúvidas</span>
        <h2 className="nu-title">Perguntas &<br />Respostas</h2>
        <div className="nu-faq-grid">
          {faqs.map((f) => (
            <div className="nu-faq-card nu-reveal" key={f.q}>
              <div className="nu-faq-q">{f.q}</div>
              <div className="nu-faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  const waMessage = "Gostaria de ativar o produto NutrIA no meu negócio";
  return (
    <section className="nu-section nu-cta" id="cta">
      <div className="nu-wrap">
        <div className="nu-cta-inner">
          <h2>Ative NutrIA<br />e pare de perder leads.</h2>
          <p>
            Comece a nutrir seus leads automaticamente com mensagens aprovadas
            e acompanhe cada conversão em tempo real.
          </p>
          <BotaoContato className="nu-btn-dark" text="Ativar NutrIA agora →" mensagem={waMessage} />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   PAGE
───────────────────────────────────────── */
export default function NutrIAPage() {
  useReveal();

  return (
    <>
      <Navbar />
      <HeroSection />
      <SobreSection />
      <ComoSection />
      <BeneficiosSection />
      <SegurancaSection />
      <PricingSection />
      <FaqSection />
      <CtaSection />
      <Footer20 className="nu-footer" voltarInicio="true" />
    </>
  );
}